"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldAlert, FileText, Sparkles, Truck, Phone, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const scenarios = [{
  title: "Trauma and unattended death scenes",
  description: "Compassionate, discreet removal of bloodborne pathogens, structural sanitization, and permanent odor elimination."
}, {
  title: "Infectious contamination and outbreaks",
  description: "Hospital-grade EPA-registered broad-spectrum neutralizers applied with electrostatic fogging and direct contact sanitization."
}, {
  title: "Chemical and hazardous spills",
  description: "Immediate perimeter stabilization, neutralizer application, and controlled vapor suppression in residential or commercial settings."
}, {
  title: "Manifest-tracked medical waste disposal",
  description: "Lawful DOT-compliant packaging in puncture-resistant containers with complete chain-of-custody documentation for health departments."
}];
const complianceBadges = [{
  label: "OSHA 29 CFR 1910.1030 Compliant"
}, {
  label: "DOT Hazardous Waste Transport Certified"
}, {
  label: "Direct Insurance Claims Coordination"
}];
export default function Overview() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.32,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="overview" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Operational Spec Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4 mb-10 text-xs font-mono text-muted-foreground uppercase tracking-wider">
          <div className="flex items-center gap-2">
            <span className="inline-block size-2 rounded-full bg-primary animate-pulse" />
            <span>24/7 Immediate Biohazard Response</span>
          </div>
          <div className="hidden sm:block">
            <span>Coverage: MD • DC • Northern VA</span>
          </div>
          <div>
            <span>No Call Centers • Local Direct Dispatch</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Context, Protocols & Regulatory Requirements */}
          <div className="lg:col-span-7 flex flex-col justify-between">
            <motion.div initial="hidden" whileInView="show" viewport={{
            once: true,
            amount: 0.2
          }} variants={fadeIn}>
              <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-primary/10 border border-primary/20 text-primary text-xs font-bold uppercase tracking-wider mb-4">
                <ShieldAlert className="size-3.5" />
                <span>Certified Biohazard Remediation</span>
              </div>

              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-foreground leading-tight mb-4">
                Certified decontamination when safety cannot be compromised
              </h2>

              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-8">
                Biological incidents, unattended deaths, and chemical exposures release hazardous pathogens that household cleaning agents cannot neutralize. TriServe deploys certified technicians equipped with Level 3 PPE, hospital-grade antimicrobial neutralizers, and negative-air filtration to isolate affected zones, protect building occupants, and return spaces to sanitary conditions.
              </p>

              {/* Remediation Scenarios Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {scenarios.map((item, i) => <div key={item.title} data-index={i} className="p-4 rounded-md border border-border bg-card shadow-xs transition-[border-color,box-shadow] hover:border-primary/50">
                    <div className="flex items-start gap-2.5 mb-1.5">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <h3 className="font-semibold text-sm text-card-foreground leading-snug">
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground pl-6.5 leading-relaxed">
                      {item.description}
                    </p>
                  </div>)}
              </div>

              {/* Safety Badges Strip */}
              <div className="flex flex-wrap gap-2.5 mb-8 pt-2 border-t border-border">
                {complianceBadges.map((badge, i) => <div key={badge.label} data-index={i} className="inline-flex items-center gap-1.5 text-xs text-foreground/80 bg-muted px-3 py-1.5 rounded-md border border-border">
                    <span className="size-1.5 rounded-full bg-primary" />
                    <span>{badge.label}</span>
                  </div>)}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap items-center gap-3">
                <Button asChild className="bg-accent text-accent-foreground font-bold hover:bg-accent/90 border border-accent">
                  <Link to="/ContactRequestServicePage">
                    Request emergency service
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>

                <Button asChild variant="outline" className="border-primary/30 text-primary hover:bg-primary/10">
                  <a href="tel:855-435-6631">
                    <Phone className="size-4 mr-1 text-primary" />
                    Call 855-435-6631
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>

          {/* Right Column: Sealed Hazardous Waste Staging Photo & Manifest Frame */}
          <div className="lg:col-span-5">
            <motion.div initial="hidden" whileInView="show" viewport={{
            once: true,
            amount: 0.2
          }} variants={fadeIn} className="sticky top-24">
              <div className="relative rounded-md border border-border bg-card p-2 shadow-md overflow-hidden">
                <div className="relative overflow-hidden rounded-sm aspect-[4/3] bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/sealed_biohazard_waste_containers_staging.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/sealed_biohazard_waste_containers_staging.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/sealed_biohazard_waste_containers_staging.png-wvc-sizes" alt="Sealed biological and medical waste containers staged with hazardous material transport manifests" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  {/* Subtle technical overlay marker */}
                  <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between px-3 py-2 rounded-sm bg-background/90 backdrop-blur-xs border border-border text-[11px] font-mono text-foreground">
                    <span className="flex items-center gap-1.5">
                      <Truck className="size-3.5 text-primary" />
                      Manifest Chain of Custody
                    </span>
                    <span className="text-muted-foreground uppercase font-semibold">
                      DOT #MD-VA-DC
                    </span>
                  </div>
                </div>

                {/* Micro Technical Spec Detail Block */}
                <div className="mt-3 p-3 bg-muted/60 rounded-sm border border-border/80">
                  <div className="flex items-start gap-2.5">
                    <FileText className="size-4 text-primary shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-foreground">
                        Documented Waste Elimination
                      </h4>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-normal">
                        Every biohazard disposal run includes an itemized manifest certifying authorized incineration at a licensed hazardous waste facility, shielding property owners from environmental liability.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}